import * as Sentry from '@sentry/react'
import { useCallback, useRef, useState } from 'react'

import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { NetworkError } from '@/services/fintalk/api/types/errors/NetworkError'
import {
  type Bot,
  listCompanyBots,
} from '@/services/fintalk/services/fintalk-backoffice/company-bots/list-by-company'

type GetCompanyBotsLoadingState = { type: 'loading' }

type GetCompanyBotsSuccessState = { type: 'success' }

type GetCompanyBotsErrorState = {
  type: 'error'
  errorType: 'NETWORK_ERROR' | 'UNKNOWN_ERROR'
  eventId?: string
  isRetrying?: boolean
}

export type GetCompanyBotsState =
  | GetCompanyBotsLoadingState
  | GetCompanyBotsSuccessState
  | GetCompanyBotsErrorState

export function useGetCompanyBots() {
  const companyId = useRouteCompanyId()!

  const [bots, setBots] = useState<Bot[]>([])
  const [botsStatus, setBotsStatus] = useState<GetCompanyBotsState>({
    type: 'loading',
  })

  const latestRequest = useRef(0)

  const loadBots = useCallback(async () => {
    const request = ++latestRequest.current

    setBots([])
    setBotsStatus((status) =>
      status.type === 'error'
        ? { ...status, isRetrying: true }
        : { type: 'loading' },
    )

    try {
      const bots = await listCompanyBots(companyId)

      if (request !== latestRequest.current) return

      setBots(bots)
      setBotsStatus({ type: 'success' })
    } catch (error) {
      if (request !== latestRequest.current) return

      if (error instanceof NetworkError) {
        setBotsStatus({ type: 'error', errorType: 'NETWORK_ERROR' })
        return
      }

      const eventId = Sentry.captureException(error)

      setBotsStatus({ type: 'error', errorType: 'UNKNOWN_ERROR', eventId })
    }
  }, [companyId])

  return { bots, botsStatus, loadBots }
}
